import React, { useEffect, useState } from 'react';
import {
  normalizeEstimatedValueRange,
  normalizePriceEstimateDistribution,
} from '../utils/formatters';

const BIN_COLORS = [
  {
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  {
    bar: 'bg-cyan-500',
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  },
  {
    bar: 'bg-blue-500',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    bar: 'bg-rose-500',
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-700 border-rose-200',
  },
];

const toFinitePrice = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => {
  const numericValue = toFinitePrice(value);
  if (numericValue === null) return 'N/A';
  const hasDecimals = Math.abs(numericValue % 1) > 0;
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });
};

const formatRangeText = (low, high) => {
  const lowValue = toFinitePrice(low);
  const highValue = toFinitePrice(high);
  if (lowValue === null && highValue === null) return 'N/A';
  if (lowValue === null) return `$${formatCurrencyValue(highValue)}`;
  if (highValue === null) return `$${formatCurrencyValue(lowValue)}`;
  if (lowValue === highValue) return `$${formatCurrencyValue(lowValue)}`;
  return `$${formatCurrencyValue(lowValue)} - $${formatCurrencyValue(highValue)}`;
};

const PriceDistributionView = ({
  rangeValue,
  distributionValue,
  maxBins = 4,
  compact = false,
  className = '',
}) => {
  const [showAllBins, setShowAllBins] = useState(false);
  const normalizedDistribution = normalizePriceEstimateDistribution({
    price_estimate_distribution: distributionValue,
  });

  const normalizedRange = normalizeEstimatedValueRange({ estimated_value_range: rangeValue })
    || normalizeEstimatedValueRange(rangeValue)
    || (normalizedDistribution.length > 0
      ? {
          low: Math.min(...normalizedDistribution.map((entry) => entry.low)),
          high: Math.max(...normalizedDistribution.map((entry) => entry.high)),
        }
      : null);

  const rangeLabel = normalizedRange
    ? formatRangeText(normalizedRange.low, normalizedRange.high)
    : 'N/A';

  const safeMaxBins = Math.max(1, maxBins);
  const isExpandable = normalizedDistribution.length > safeMaxBins;
  const displayBins = showAllBins ? normalizedDistribution : normalizedDistribution.slice(0, safeMaxBins);
  const hiddenBinCount = Math.max(0, normalizedDistribution.length - displayBins.length);
  const titleSizeClass = compact ? 'text-[10px]' : 'text-[11px]';
  const valueSizeClass = compact ? 'text-[10px]' : 'text-xs';
  const barHeightClass = compact ? 'h-1.5' : 'h-2';
  const rowGapClass = compact ? 'space-y-1' : 'space-y-1.5';

  useEffect(() => {
    if (!isExpandable) {
      setShowAllBins(false);
    }
  }, [isExpandable]);

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`${titleSizeClass} font-semibold uppercase tracking-wide text-slate-500`}>
          Price Range
        </span>
        <span className={`${valueSizeClass} font-semibold text-emerald-700 tabular-nums`}>
          {rangeLabel}
        </span>
      </div>

      {normalizedDistribution.length > 0 ? (
        <>
          <div className={`flex w-full overflow-hidden rounded-full bg-slate-200/80 ${barHeightClass}`}>
            {normalizedDistribution.map((entry, index) => {
              const color = BIN_COLORS[index % BIN_COLORS.length];
              const width = Math.max(0, entry.probability * 100);
              return (
                <div
                  key={`${entry.low}-${entry.high}-${index}`}
                  className={`${color.bar} h-full`}
                  style={{ width: `${width}%` }}
                  title={`${formatRangeText(entry.low, entry.high)} • ${(entry.probability * 100).toFixed(1)}%`}
                />
              );
            })}
          </div>

          <div className={rowGapClass}>
            {displayBins.map((entry, index) => {
              const color = BIN_COLORS[index % BIN_COLORS.length];
              return (
                <div key={`${entry.low}-${entry.high}-${index}`} className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${color.dot}`} />
                  <span className={`${valueSizeClass} flex-1 text-slate-600 tabular-nums`}>
                    {formatRangeText(entry.low, entry.high)}
                  </span>
                  <span className={`${valueSizeClass} rounded border px-1.5 py-0.5 font-semibold ${color.badge}`}>
                    {(entry.probability * 100).toFixed(0)}%
                  </span>
                </div>
              );
            })}
            {isExpandable && (
              <button
                type="button"
                onClick={() => setShowAllBins((previous) => !previous)}
                className={`${valueSizeClass} text-left font-medium text-slate-500 hover:text-slate-700 transition-colors`}
              >
                {showAllBins ? 'Show fewer bins' : `+${hiddenBinCount} additional bins`}
              </button>
            )}
          </div>
        </>
      ) : (
        <div className={`${valueSizeClass} text-slate-400`}>Distribution unavailable</div>
      )}
    </div>
  );
};

export default PriceDistributionView;
